import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api, type KnotListEntry } from '@/lib/api';
import {
  drainPending,
  enqueuePending,
  getClientId,
  listFavorites,
  persistStorageIfPossible,
  sendPrecacheMessage,
  sendUncacheMessage,
  setFavoriteLocal,
} from '@/lib/favorites';
import { STARTER_KNOTS } from '@/lib/starterKnots';

const MANIFEST_URL = 'https://asset.freedomboatclub.ai/manifest.json';

async function collectAssetUrls(slug: string): Promise<string[]> {
  try {
    const res = await fetch(MANIFEST_URL, { cache: 'no-store' });
    if (!res.ok) return [];
    const manifest = (await res.json()) as {
      knots?: Record<string, { url?: string } | undefined>;
    };
    const entry = manifest.knots?.[slug];
    if (!entry?.url) return [];
    const urls = new Set<string>([MANIFEST_URL, entry.url]);
    try {
      const knotRes = await fetch(entry.url);
      if (knotRes.ok) {
        const knot = (await knotRes.json()) as {
          frames?: Array<{ imageUrl?: string }>;
        };
        for (const f of knot.frames ?? []) {
          if (f.imageUrl) urls.add(f.imageUrl);
        }
      }
    } catch {
      // knot JSON fetch failed — still precache the manifest + versioned URL itself
    }
    return [...urls];
  } catch {
    return [];
  }
}

export default function KnotLibrary({ onSignedOut }: { onSignedOut: () => void }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [knots, setKnots] = useState<KnotListEntry[]>([]);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<Set<string>>(new Set());
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [seedStatus, setSeedStatus] = useState<string | null>(null);

  const flushPending = useCallback(async () => {
    const clientId = await getClientId();
    await drainPending(async ({ slug, action }) => {
      try {
        if (action === 'favorite') await api.addFavorite(slug, clientId);
        else await api.removeFavorite(slug, clientId);
        return true;
      } catch {
        return false;
      }
    });
  }, []);

  useEffect(() => {
    api
      .listKnots()
      .then((r) => {
        setKnots(r.knots);
        setLoading(false);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load knots');
        setLoading(false);
      });
    listFavorites().then((slugs) => setFavorites(new Set(slugs)));
  }, []);

  useEffect(() => {
    flushPending();
    const onOnline = () => flushPending();
    const onVis = () => {
      if (document.visibilityState === 'visible') flushPending();
    };
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [flushPending]);

  async function toggleFavorite(k: KnotListEntry) {
    if (busy.has(k.slug)) return;
    setBusy((b) => new Set(b).add(k.slug));
    const isFav = favorites.has(k.slug);
    const next = new Set(favorites);
    if (isFav) next.delete(k.slug);
    else next.add(k.slug);
    setFavorites(next);

    try {
      const clientId = await getClientId();
      if (!isFav) {
        await setFavoriteLocal(k.slug, true, k.version);
        await persistStorageIfPossible();
        const urls = await collectAssetUrls(k.slug);
        await sendPrecacheMessage(urls);
        try {
          await api.addFavorite(k.slug, clientId);
        } catch {
          await enqueuePending(k.slug, 'favorite');
        }
      } else {
        await setFavoriteLocal(k.slug, false);
        const urls = await collectAssetUrls(k.slug);
        await sendUncacheMessage(urls);
        try {
          await api.removeFavorite(k.slug, clientId);
        } catch {
          await enqueuePending(k.slug, 'unfavorite');
        }
      }
    } finally {
      setBusy((b) => {
        const next = new Set(b);
        next.delete(k.slug);
        return next;
      });
    }
  }

  async function onLogout() {
    await api.logout().catch(() => {});
    onSignedOut();
    navigate('/login', { replace: true });
  }

  async function onSeedStarters() {
    if (seeding) return;
    setSeeding(true);
    setSeedStatus(null);
    try {
      const existingSlugs = new Set(knots.map((k) => k.slug));
      const toCreate = STARTER_KNOTS.filter((s) => !existingSlugs.has(s.slug));
      if (toCreate.length === 0) {
        setSeedStatus('All 8 starters already exist.');
        return;
      }
      const created: KnotListEntry[] = [];
      for (const s of toCreate) {
        try {
          const { knot } = await api.createKnot({ name: s.name, mode: s.mode, category: s.category });
          created.push({
            id: knot.id,
            slug: knot.slug,
            name: knot.name,
            category: knot.category,
            mode: knot.mode,
            version: knot.version,
            contentHash: null,
            publishedAt: null,
            updatedAt: knot.updatedAt,
          });
        } catch (err) {
          console.warn('[seed] create failed for', s.slug, err);
        }
      }
      setKnots((prev) => [...created, ...prev]);
      setSeedStatus(`Created ${created.length} starter${created.length === 1 ? '' : 's'}.`);
    } finally {
      setSeeding(false);
    }
  }

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    if (!newName.trim() || creating) return;
    setCreating(true);
    try {
      const { knot } = await api.createKnot({ name: newName.trim(), mode: 'stopmotion' });
      navigate(`/knots/${knot.id}/edit`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create knot');
      setCreating(false);
    }
  }

  return (
    <div className="min-h-full flex flex-col">
      <header className="border-b bg-card">
        <div className="container flex h-14 items-center justify-between">
          <div className="font-semibold tracking-tight">FBC Knot Builder</div>
          <Button variant="outline" size="sm" onClick={onLogout}>
            Sign out
          </Button>
        </div>
      </header>
      <main className="container flex-1 py-10 space-y-8">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Knot library</h1>
          <p className="text-sm text-muted-foreground">
            Create a new knot, or pick one to keep authoring. All knots are filmed and uploaded as stop-motion frames.
          </p>
        </div>

        <form onSubmit={onCreate} className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-4">
          <div className="flex-1 min-w-[240px] space-y-1">
            <label htmlFor="name" className="text-xs font-medium text-muted-foreground">
              Name
            </label>
            <Input
              id="name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Palomar"
              autoComplete="off"
            />
          </div>
          <Button type="submit" disabled={creating || newName.trim().length === 0}>
            {creating ? 'Creating…' : 'New knot'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onSeedStarters}
            disabled={seeding}
            title="Create drafts for Palomar, Improved Clinch, Uni, Surgeon's, Dropper Loop, FG Knot, Bimini Twist, Albright"
          >
            {seeding ? 'Seeding…' : 'Seed v1 starters'}
          </Button>
          {seedStatus ? (
            <span className="text-xs text-muted-foreground">{seedStatus}</span>
          ) : null}
        </form>

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : knots.length === 0 ? (
          <div className="rounded-lg border border-dashed p-12 text-center text-sm text-muted-foreground">
            No knots yet. Create your first above.
          </div>
        ) : (
          <ul className="divide-y rounded-lg border bg-card">
            {knots.map((k) => {
              const fav = favorites.has(k.slug);
              const isBusy = busy.has(k.slug);
              const published = !!k.publishedAt;
              return (
                <li key={k.id} className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => toggleFavorite(k)}
                      disabled={isBusy || !published}
                      title={
                        !published
                          ? 'Publish this knot before you can favorite it for offline'
                          : fav
                            ? 'Remove from offline favorites'
                            : 'Favorite for offline'
                      }
                      aria-pressed={fav}
                      className={
                        'flex h-7 w-7 items-center justify-center rounded-full border transition ' +
                        (fav
                          ? 'border-rose-400 bg-rose-100 text-rose-600 hover:bg-rose-200'
                          : 'border-muted bg-background text-muted-foreground hover:bg-muted disabled:opacity-40')
                      }
                    >
                      {fav ? '♥' : '♡'}
                    </button>
                    <div className="space-y-0.5">
                      <Link to={`/knots/${k.id}/edit`} className="text-sm font-medium hover:underline">
                        {k.name}
                      </Link>
                      <div className="text-xs text-muted-foreground">
                        {k.category} · updated {new Date(k.updatedAt).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {published ? `v${k.version}` : 'draft'}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </main>
    </div>
  );
}
